import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { LocaleCode } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { usePlausibilityQuery, useProfitAndLossQuery } from '@/api/queries';
import { PATH } from '@/router/PATH';
import {
    defaultPnlRange,
    describeWindow,
    formatGeneratedAt,
    resolveLocale,
    todayIso,
    toStatementRows,
    unwrapStatement,
    toWarningNotices,
    type WarningNotice,
    toUnmappedCodes,
    warningLineIdSet,
    type StatementColumns,
    type StatementRow,
} from '../statementMeta';
import { useStatementsRange } from '../statementsOutletContext';

export type UseProfitLossPageResult = {
    locale: LocaleCode;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    rows: StatementRow[] | null;
    /** Column headers, written from the server's own windows. */
    columns: StatementColumns;
    warningNotices: WarningNotice[];
    /**
     * Postable accounts the ledger's statement layout maps to no line, so their
     * balances appear nowhere on this statement. Server-reported; the statement
     * is incomplete until the layout covers them.
     */
    unmappedCodes: string[];
    onDismissWarnings: () => void;
    onViewLedger: (accountId: string) => void;
    generatedText: string;
};

/**
 * Drives the Profit & Loss page: fetches the statement for a period, turns it
 * into render rows (pure), and owns the range / preset / drill / warning state.
 * The default range is the current fiscal year to date.
 *
 * The same two-request split and the same advisory rules as the Balance Sheet —
 * see `useBalanceSheetPage`. Plausibility is keyed on the range's **end** date,
 * which is the position the advisories are computed at.
 *
 * Nothing here sums, re-signs or recomputes a figure.
 */
export const useProfitLossPage = (): UseProfitLossPageResult => {
    const { t, i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const navigate = useNavigate();

    const today = React.useRef(todayIso()).current;
    // The Statements shell owns the period. A P&L reports **over** a window, so
    // unlike the Balance Sheet it takes both ends. The old default — the fiscal
    // year to date — is the floor when the page is rendered outside the shell.
    const { fromDate, toDate } = useStatementsRange();
    const fallback = defaultPnlRange(today);
    const range = {
        from: fromDate || fallback.from,
        to: toDate || fallback.to,
    };
    const [warningsDismissed, setWarningsDismissed] = React.useState(false);
    const generatedText = React.useRef(formatGeneratedAt(new Date())).current;

    const query = useProfitAndLossQuery(
        range.from,
        range.to,
        locale,
        'PRIOR_YEAR',
    );
    const { data, error } = unwrapStatement(query.data, query.error);
    const plausibility = usePlausibilityQuery(range.to, locale);
    // Memoised because it feeds a `useMemo` below: a bare `?? []` mints a
    // fresh array on every render, so every dependent recomputes forever.
    const warnings = React.useMemo(
        () =>
            plausibility.data?.success
                ? plausibility.data.response.warnings
                : [],
        [plausibility.data],
    );

    // A fresh range is a fresh statement — un-dismiss its advisories.
    React.useEffect(() => {
        setWarningsDismissed(false);
    }, [range.from, range.to]);

    const rows = React.useMemo(() => {
        if (!data) {
            return null;
        }
        return toStatementRows(data.lines, warningLineIdSet(warnings));
    }, [data, warnings]);

    /**
     * A P&L spans a range, so both columns are headed with their range —
     * "01/01/2026 – 30/07/2026" against "01/01/2025 – 30/07/2025". Both windows
     * come from the response.
     */
    const columns = React.useMemo<StatementColumns>(() => {
        const label = (window: ReturnType<typeof describeWindow>) =>
            window?.from && window.to
                ? t('statements.columns.rangeDates', {
                      from: window.from,
                      to: window.to,
                  })
                : t('statements.columns.amount');
        const prior = describeWindow(data?.comparativeWindow ?? null);
        return {
            amount: label(describeWindow(data?.window ?? null)),
            prior: prior ? label(prior) : null,
            delta: prior ? t('statements.columns.deltaVsPrior') : null,
        };
    }, [data, t]);

    // Grouped, not concatenated: the server sends one advisory per unmapped
    // account and a chart past its layout produces dozens. Those are dropped
    // here and stated once, from the statement's own list, below.
    const notices = React.useMemo(() => toWarningNotices(warnings), [warnings]);
    const warningNotices = warningsDismissed ? [] : notices;

    // From the **statement**, not the advisories — it is scoped to this
    // statement and it survives a failed plausibility call.
    const unmappedCodes = React.useMemo(
        () => toUnmappedCodes(data?.unmappedAccounts ?? []),
        [data],
    );

    const onRetry = React.useCallback(() => {
        query.refetch();
        plausibility.refetch();
    }, [query, plausibility]);

    // The Journal **tab**, carrying the window the figure was read over.
    const onViewLedger = React.useCallback(
        (accountId: string) => {
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ACCOUNT(accountId, {
                    from: range.from,
                    to: range.to,
                }),
            );
        },
        [navigate, range.from, range.to],
    );

    return {
        locale,
        isLoading: query.isLoading,
        error,
        onRetry,
        rows,
        columns,
        warningNotices,
        // Not dismissible: an incomplete statement stays incomplete, and this
        // is the only thing on the page that says so.
        unmappedCodes,
        onDismissWarnings: () => setWarningsDismissed(true),
        onViewLedger,
        generatedText,
    };
};
